/**
 * API Service
 * Handles all HTTP requests to the backend
 * Uses Axios for request/response handling
 */

import axios from 'axios';

// API base URL from environment or default
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 600000, // 10 minutes timeout for very large file uploads (500MB videos)
  headers: {
    'Accept': 'application/json'
  }
});

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('[API] Response error:', error);
    
    // Transform error to consistent format
    if (error.response) {
      const { data, status } = error.response;
      const apiError = new Error(data?.error?.message || 'Request failed');
      apiError.code = data?.error?.code || `HTTP_${status}`;
      apiError.status = status;
      apiError.details = data?.error?.details;
      return Promise.reject(apiError);
    }
    
    if (error.request) {
      const networkError = new Error('Network error - please check your connection');
      networkError.code = 'NETWORK_ERROR';
      return Promise.reject(networkError);
    }
    
    return Promise.reject(error);
  }
);

/**
 * Uploads an audio file for transcription and analysis
 * @param {File} file - Audio file to upload
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<Object>} Upload result with transcription, summary, tasks, email
 */
export async function uploadAudio(file, onProgress) {
  const formData = new FormData();
  formData.append('audio', file);

  const response = await apiClient.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    }
  });

  return response.data;
}

/**
 * Gets supported audio formats
 * @returns {Promise<Object>} Supported formats info
 */
export async function getSupportedFormats() {
  const response = await apiClient.get('/upload/formats');
  return response.data;
}

/**
 * Checks API health status
 * @returns {Promise<Object>} Health status
 */
export async function checkHealth() {
  const response = await apiClient.get('/health', { baseURL: API_URL.replace('/api', '') });
  return response.data;
}

/**
 * Uploads a file for async processing
 * Returns immediately with a job ID
 * @param {File} file - Audio/video file to upload
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<Object>} Job info with jobId and pollUrl
 */
export async function uploadAudioAsync(file, onProgress) {
  const formData = new FormData();
  formData.append('audio', file);

  const response = await apiClient.post('/upload/async', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    }
  });

  return response.data;
}

/**
 * Gets job status
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Job status
 */
export async function getJobStatus(jobId) {
  const response = await apiClient.get(`/jobs/${jobId}`);
  return response.data;
}

/**
 * Gets job result (polls until completed)
 * @param {string} jobId - Job ID
 * @param {Function} onStatusChange - Callback for status updates
 * @param {number} pollInterval - Polling interval in ms (default: 3000)
 * @returns {Promise<Object>} Job result
 */
export async function getJobResult(jobId, onStatusChange = null, pollInterval = 3000) {
  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const response = await apiClient.get(`/jobs/${jobId}/result`);
        const data = response.data;
        
        if (onStatusChange) {
          onStatusChange(data.data);
        }

        // If completed, resolve
        if (data.data.status === 'completed') {
          resolve(data.data);
          return;
        }

        // If failed, reject
        if (data.data.status === 'failed') {
          reject(new Error(data.data.error?.message || 'Processing failed'));
          return;
        }

        // Still processing, poll again
        setTimeout(poll, pollInterval);

      } catch (error) {
        reject(error);
      }
    };

    poll();
  });
}

/**
 * Transcribe file only (no analysis)
 * @param {File} file - Audio/video file
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} Transcription result
 */
export async function transcribeFile(file, onProgress) {
  const formData = new FormData();
  formData.append('audio', file);

  const response = await apiClient.post('/transcribe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    }
  });

  return response.data;
}

/**
 * Create a chat conversation with transcription
 * @param {string} transcription - The transcription text
 * @param {Object} metadata - Optional metadata
 * @returns {Promise<Object>} Conversation info
 */
export async function createConversation(transcription, metadata = {}) {
  const response = await apiClient.post('/chat/conversations', {
    transcription,
    metadata
  });
  return response.data;
}

/**
 * Send message to conversation
 * @param {string} conversationId 
 * @param {string} message 
 * @returns {Promise<Object>} Response
 */
export async function sendMessage(conversationId, message) {
  const response = await apiClient.post(`/chat/conversations/${conversationId}/messages`, {
    message
  });
  return response.data;
}

/**
 * Get conversation history
 * @param {string} conversationId 
 * @returns {Promise<Object>}
 */
export async function getConversation(conversationId) {
  const response = await apiClient.get(`/chat/conversations/${conversationId}`);
  return response.data;
}

/**
 * Generate quick summary
 * @param {string} transcription 
 * @param {string} type - summary type
 * @returns {Promise<Object>}
 */
export async function generateSummary(transcription, type = 'executive') {
  const response = await apiClient.post('/chat/summarize', {
    transcription,
    type
  });
  return response.data;
}

export default apiClient;
